// Talent Management — tab 2: Searches.
//
// The OPERATIONAL lane, deliberately unlike tab 1. The dashboard answers "how is
// talent responding, on average, over a window"; this answers "what did SCOUT do
// on THIS search, and why". Per-run product truth, read straight from Postgres.
//
// There is no aggregate on this surface on purpose — no rate, no average, no
// "searches this week". Those are metrics, and metrics have exactly one definition,
// in Dataform. Adding one here would create a second.
//
// It reads ONE table (`scout_searches`, migration 0105) covering BOTH SCOUT
// surfaces: the live concierge outreach flow and the guest results carousel. Until
// that table existed the page read the buyer's `search_runs`, which only the guest
// funnel ever writes — so the live flow, the one that actually contacts talent, was
// invisible here and the list looked stale and three-picks-long.
//
// The FILTERS and the search box run SERVER-side, across the whole feed: the
// list is keyset-paginated, so a browser-side filter could only ever cover the
// pages already fetched (which is exactly what it used to do). SORTING stays in
// the browser over the loaded pages — "the highest score" means the highest
// among what is loaded, and the toolbar says how much that is — because a
// server-side sort contract is a different, heavier promise for a list an
// operator scans a page at a time.
import {
  Badge,
  Button,
  DateRangeFilter,
  EMPTY_DATE_RANGE,
  facetOptions,
  Input,
  isDateRangeActive,
  SortableTh,
  TableExport,
  TableFilters,
  type DateRange,
  type FilterColumn,
  useColumnFilters,
  useRestoredState,
  useTableSort,
} from "@recruiter/shared";
import type { AdminSearchDetail, AdminSearchPick, AdminSearchRow } from "@recruiter/shared/types";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";

import { AreaBoundary } from "../components/area-gate";
import { BackLink } from "../components/back-link";
import { SectionTabs, TALENT_TABS } from "../components/section-tabs";
import { ArrowRightIcon } from "../components/icons";
import { EmptyState, LoadingState, TableSkeleton } from "../components/states";
import { ExplorerProjectLink, ExplorerTalentLink } from "../components/explorer-link";
import { WorkShapeTag } from "../components/work-shape-tag";
import { formatDateTime } from "../lib/format";
import { talentSearchQuery, talentSearchesQuery } from "../lib/queries";
import { useDebounced } from "../lib/use-debounced";
import { WORK_SHAPES, workShapeLabel } from "../lib/work-shape";
import { useCountryFilterOptions } from "../components/country-filter";
import { UNKNOWN_COUNTRY } from "../lib/country";
import { DEVICE_FILTER_OPTIONS } from "../lib/device";
import "./searches.css";

/** The search's 0..100 combined fit+quality score.
 *
 *  Unknown renders as a dash, never 0: a search that ranked nobody has no score,
 *  and showing 0 would sort it alongside a search that found five terrible
 *  talents. Those are different outcomes. */
function Score({ value }: { value: number | null }) {
  if (value === null) {
    return (
      <span className="tm-unknown" title="No shortlist to score">
        -
      </span>
    );
  }
  const tone = value >= 75 ? "success" : value >= 55 ? "neutral" : "warning";
  return (
    <Badge tone={tone} size="sm" title="Combined fit and quality, 0-100">
      {value.toFixed(0)}
    </Badge>
  );
}

/** A talent, linked to their page.
 *
 *  `projectId` scopes the link to this search's project, so landing on the talent
 *  page opens THAT engagement rather than making the reader hunt for it in a list
 *  of everything we ever approached them for. */
function TalentLink({ sellerId, projectId }: { sellerId: string; projectId?: string }) {
  return (
    <ExplorerTalentLink sellerId={sellerId} projectId={projectId} className="cs-primary-link">
      {sellerId}
    </ExplorerTalentLink>
  );
}

/** The human name for a search surface. ONE mapper, used by the badge, by the
 *  surface filter's options, and by the sort accessor, so a column cannot order or
 *  filter by a different name than the one it prints. */
function surfaceLabel(surface: string): string {
  if (surface === "concierge") return "Outreach";
  if (surface === "funnel") return "Browse";
  return surface;
}

/** Which product the search served. Both surfaces run the same SCOUT loop, so an
 *  operator comparing two rows needs to know which one they are reading: the
 *  concierge flow contacts talent, the guest funnel only shows cards. */
function SurfaceBadge({ surface }: { surface: string }) {
  if (!surface) return <span className="tm-unknown">-</span>;
  return (
    <Badge tone="neutral" size="sm" title={`Search surface: ${surface}`}>
      {surfaceLabel(surface)}
    </Badge>
  );
}

// ── The list ────────────────────────────────────────────────────────────────

/** Column id → the value that column orders by.
 *
 *  Declared as extractors rather than field names so a column can sort on
 *  something other than what it prints: "Where" orders by the LABEL a reader sees
 *  ("Browse" before "Outreach"), not by the raw `funnel`/`concierge` key. A null
 *  score and a blank project name sort LAST in both directions (`table-sort`),
 *  which is the point: a search that ranked nobody has no score, and burying it
 *  under the scored ones only when sorting one way would be arbitrary. */
const SEARCH_SORT = {
  when: (r: AdminSearchRow) => r.createdAt,
  project: (r: AdminSearchRow) => r.projectName,
  looking: (r: AdminSearchRow) => r.querySummary,
  where: (r: AdminSearchRow) => surfaceLabel(r.surface),
  score: (r: AdminSearchRow) => r.searchScore,
  considered: (r: AdminSearchRow) => r.poolCount,
  shortlist: (r: AdminSearchRow) => r.rankedCount,
} as const;

/** Outcome is the `degraded` flag promoted to a control, because an empty
 *  shortlist has two very different causes and the Shortlist column can only
 *  badge them one row at a time. "Show me every search the gateway ate" is the
 *  question somebody debugging a bad week actually asks, and it is CLOSED so
 *  "nothing failed" reads as an answer rather than a control that went missing. */
const SEARCH_OUTCOME_OPTIONS = [
  { value: "degraded", label: "Search failed" },
  { value: "ok", label: "Completed" },
] as const;

/** The work-shape filter's fixed options: the three latched shapes plus the
 *  not-classified bucket (`none` is the value the server filter takes for it). */
const WORK_SHAPE_OPTIONS = [...WORK_SHAPES, "none"].map((value) => ({
  value,
  label: workShapeLabel(value),
}));

export function SearchesPage() {
  const [filter, setFilter] = useRestoredState("searches/filter", "");
  const [surfaces, setSurfaces] = useRestoredState<readonly string[]>("searches/surfaces", []);
  const [outcomes, setOutcomes] = useRestoredState<readonly string[]>("searches/outcomes", []);
  const [shapes, setShapes] = useRestoredState<readonly string[]>("searches/shapes", []);
  // The project's creation stamps (device, origin country), the console-wide
  // filters, narrowing SERVER-side like the three above.
  const [devices, setDevices] = useRestoredState<readonly string[]>("searches/devices", []);
  const [countries, setCountries] = useRestoredState<readonly string[]>("searches/countries", []);
  const countryOptions = useCountryFilterOptions();
  // The cohort range. Local state like the filters beside it, and it narrows
  // SERVER-side for the same reason they do: this list is keyset-paged, so a
  // browser-side date filter could only ever cover the pages already fetched.
  const [range, setRange] = useRestoredState<DateRange>("searches/range", EMPTY_DATE_RANGE);
  // Debounced so a keystroke does not become a request: the text box narrows
  // SERVER-side now, like the two filters beside it.
  const q = useDebounced(filter.trim(), 300);
  const searches = useInfiniteQuery(
    talentSearchesQuery({ surfaces, outcomes, shapes, devices, countries, q, range }),
  );

  const loaded: AdminSearchRow[] = useMemo(
    () => searches.data?.pages.flatMap((p) => p.searches) ?? [],
    [searches.data],
  );

  /** Where the search ran, and whether it really ran. The other columns are a
   *  date, a project, free text, or a number, which the search box and the sort
   *  caret already serve.
   *
   *  Surface options come from the response's store-wide facet, not a
   *  hard-coded pair: the backend may log a third surface before this page
   *  hears about it, and an option list that silently omits it would hide
   *  those rows behind a filter nobody can clear. Both selections travel to
   *  the SERVER, where they narrow the whole feed rather than the loaded
   *  pages. */
  const knownSurfaces = searches.data?.pages[0]?.surfaces;
  const columns = useMemo<readonly FilterColumn<AdminSearchRow>[]>(
    () => [
      {
        key: "surface",
        label: "Filter by surface",
        allLabel: "All surfaces",
        value: (r) => r.surface,
        optionLabel: surfaceLabel,
        options: facetOptions(knownSurfaces ?? [], surfaceLabel),
        selected: surfaces,
        onChange: setSurfaces,
      },
      {
        key: "outcome",
        label: "Filter by outcome",
        allLabel: "Any outcome",
        value: (r) => (r.degraded ? "degraded" : "ok"),
        options: SEARCH_OUTCOME_OPTIONS,
        selected: outcomes,
        onChange: setOutcomes,
      },
      {
        key: "workShape",
        label: "Filter by work shape",
        allLabel: "All work shapes",
        // Null IS "none" server-side (the not-classified bucket), so the local
        // pass over the loaded rows agrees with the SQL narrowing.
        value: (r) => r.workShape ?? "none",
        options: WORK_SHAPE_OPTIONS,
        selected: shapes,
        onChange: setShapes,
      },
      {
        key: "device",
        label: "Filter by device",
        allLabel: "All devices",
        // Null IS "unknown" server-side (no stamp recorded), so the local pass
        // over the loaded rows agrees with the SQL narrowing.
        value: (r) => r.createdDevice ?? "unknown",
        options: DEVICE_FILTER_OPTIONS,
        selected: devices,
        onChange: setDevices,
      },
      {
        key: "country",
        label: "Filter by country",
        allLabel: "All countries",
        value: (r) => r.originCountry ?? UNKNOWN_COUNTRY,
        options: countryOptions,
        selected: countries,
        onChange: setCountries,
      },
    ],
    [
      knownSurfaces,
      surfaces,
      outcomes,
      shapes,
      devices,
      countries,
      countryOptions,
      setSurfaces,
      setOutcomes,
      setShapes,
      setDevices,
      setCountries,
    ],
  );
  const filters = useColumnFilters(loaded, columns);
  const rows = filters.rows;
  const sorted = useTableSort(rows, SEARCH_SORT, null, { restore: "searches/sort" });
  const narrowed = filter.trim() !== "" || filters.active || isDateRangeActive(range);

  if (searches.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary
          area="talent_management"
          error={searches.error}
          onRetry={() => void searches.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Searches</h1>
        <p className="ad-page__sub">
          Every talent search, newest first. Open one to see what Mira was looking for and why it
          picked each talent.
        </p>
      </header>

      <SectionTabs label="Talent Management sections" tabs={TALENT_TABS} />

      <div className="rc-toolbar sr-toolbar">
        <Input
          type="search"
          inputSize="sm"
          placeholder="Filter by project or what Mira looked for…"
          aria-label="Filter searches"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
        <DateRangeFilter label="Ran" value={range} onChange={setRange} />
        <TableFilters
          controls={filters.controls}
          active={narrowed}
          onClear={() => {
            setFilter("");
            filters.clear();
            setRange(EMPTY_DATE_RANGE);
          }}
        />
        {/* Says "loaded", not "searches". This list is keyset-paginated, so
            SORTING reaches the pages that have been fetched and no further: the
            top score here is the top score SO FAR. The filters and the search
            box, though, run server-side and cover the whole feed — every loaded
            row already matches them. */}
        {searches.data ? (
          <span className="rc-toolbar__count">
            {sorted.rows.length.toLocaleString()} of {loaded.length.toLocaleString()} loaded
            {searches.hasNextPage ? ", more available" : ""}
          </span>
        ) : null}
      </div>

      {searches.isPending ? (
        <TableSkeleton columns={6} />
      ) : loaded.length === 0 && !narrowed ? (
        <EmptyState
          title="No searches yet"
          message="When a project runs a talent search, it will appear here."
        />
      ) : sorted.rows.length === 0 ? (
        <EmptyState
          title="No searches match"
          message="The whole feed was searched. Try clearing a filter."
        />
      ) : (
        <>
          <div className="rc-table-wrap">
            <table className="rc-table sr-table">
              <TableExport name="searches" label="searches" />
              <thead>
                <tr>
                  <SortableTh sortKey="when" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    When
                  </SortableTh>
                  <SortableTh sortKey="project" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Project
                  </SortableTh>
                  <SortableTh sortKey="looking" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Looking for
                  </SortableTh>
                  <SortableTh sortKey="where" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Where
                  </SortableTh>
                  <SortableTh
                    sortKey="score"
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                    numeric
                  >
                    Score
                  </SortableTh>
                  <SortableTh
                    sortKey="considered"
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                    numeric
                  >
                    Considered
                  </SortableTh>
                  <SortableTh
                    sortKey="shortlist"
                    ariaSort={sorted.ariaSort}
                    onToggle={sorted.toggle}
                    numeric
                  >
                    Shortlist
                  </SortableTh>
                  <th>
                    <span className="ad-visually-hidden">Open</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.rows.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link
                        to="/talent-management/searches/$searchId"
                        params={{ searchId: r.id }}
                        className="cs-primary-link"
                      >
                        {formatDateTime(r.createdAt ?? null)}
                      </Link>
                    </td>
                    <td>
                      <span className="ex-title-line">
                        {/* The name walks to the project itself; the date link on
                            the left stays the way into THIS search. Plain text
                            for a viewer the Explorer does not open for. */}
                        <ExplorerProjectLink projectId={r.projectId} className="cs-primary-link">
                          {r.projectName || <span className="tm-unknown">Untitled</span>}
                        </ExplorerProjectLink>
                        {/* The project's engagement shape, same tag as the
                            Explorer, so a search reads the same as its row. */}
                        <WorkShapeTag shape={r.workShape} />
                      </span>
                    </td>
                    <td className="sr-summary">
                      {r.querySummary || <span className="tm-unknown">-</span>}
                    </td>
                    <td>
                      <SurfaceBadge surface={r.surface} />
                    </td>
                    <td className="rc-num">
                      <Score value={r.searchScore ?? null} />
                    </td>
                    <td className="rc-num">{r.poolCount.toLocaleString()}</td>
                    <td className="rc-num">
                      {r.rankedCount.toLocaleString()}
                      {/* An empty shortlist has two very different causes and the
                          row must not blur them: nobody fit, or the gateway was
                          down and we never really looked. */}
                      {r.degraded ? (
                        <Badge tone="danger" size="sm" className="rc-count-badge">
                          Search failed
                        </Badge>
                      ) : null}
                    </td>
                    <td>
                      <Link
                        to="/talent-management/searches/$searchId"
                        params={{ searchId: r.id }}
                        className="cs-row-link"
                        aria-label={`Open search from ${formatDateTime(r.createdAt ?? null)}`}
                      >
                        <ArrowRightIcon size={16} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {searches.hasNextPage ? (
            <div className="cs-load-more">
              <Button
                type="button"
                variant="outline"
                tone="neutral"
                size="sm"
                disabled={searches.isFetchingNextPage}
                onClick={() => void searches.fetchNextPage()}
              >
                {searches.isFetchingNextPage ? "Loading…" : "Load more searches"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ── The detail ──────────────────────────────────────────────────────────────

/** Human labels for the intent snapshot's raw keys. An unmapped key prints its own
 *  name rather than being dropped: the snapshot is whatever the extractor produced,
 *  and hiding a field an operator needs is worse than showing an ugly label. */
const INTENT_LABELS: Record<string, string> = {
  summary: "Summary",
  primary_skills: "Skills searched",
  must_have: "Must have",
  core_deliverable: "Core deliverable",
  budget_max: "Budget ceiling",
  budget_min: "Budget floor",
  delivery_max_days: "Delivery limit (days)",
};

function Requirements({ detail }: { detail: AdminSearchDetail }) {
  const p = detail.preferences;
  // `summary` is rendered on its own above as the search brief; repeating it in the
  // fact list would just be the same sentence twice.
  const intentRows = Object.entries(detail.intent).filter(([k]) => k !== "summary");

  const prefRows: { label: string; value: string | null }[] = p
    ? [
        { label: "Languages", value: p.languages.length ? p.languages.join(", ") : null },
        {
          label: "Preferred countries",
          value: p.countriesPreferred.length ? p.countriesPreferred.join(", ") : null,
        },
        {
          label: "Required countries",
          value: p.requiredCountries.length ? p.requiredCountries.join(", ") : null,
        },
        {
          label: "Excluded talents",
          value: p.excludedSellers.length ? p.excludedSellers.join(", ") : null,
        },
      ].filter((r) => r.value !== null)
    : [];

  if (intentRows.length === 0 && prefRows.length === 0 && !p?.clientNotes.length) {
    return <p className="sr-empty">No requirements were recorded for this search.</p>;
  }

  return (
    <>
      <dl className="sr-facts">
        {intentRows.map(([key, value]) => (
          <div key={key}>
            <dt>{INTENT_LABELS[key] ?? key}</dt>
            <dd>{value}</dd>
          </div>
        ))}
        {prefRows.map((f) => (
          <div key={f.label}>
            <dt>{f.label}</dt>
            <dd>{f.value}</dd>
          </div>
        ))}
      </dl>
      {p?.clientNotes.length ? (
        <>
          <h3 className="sr-subhead">In the client's own words</h3>
          <ul className="sr-notes">
            {p.clientNotes.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </ul>
        </>
      ) : null}
      {/* These ARE a snapshot, unlike the old panel which re-read the project's
          live preferences row and had to warn the reader it might have moved. */}
      <p className="sr-note">
        Captured when this search ran, so later edits to the project do not change what you see
        here.
      </p>
    </>
  );
}

/** The four per-category grades behind the craft tier. Rendered only when SCOUT
 *  emitted them, so an older row shows nothing rather than four empty chips. */
function Scorecard({ pick }: { pick: AdminSearchPick }) {
  const grades: { label: string; value: string }[] = [
    { label: "Discipline", value: pick.disciplineFit },
    { label: "Task", value: pick.taskFit },
    { label: "Price", value: pick.priceFit },
    { label: "Timeline", value: pick.timelineFit },
  ].filter((g) => g.value);
  if (grades.length === 0) return null;
  return (
    <span className="sr-chips">
      {grades.map((g) => (
        <Badge key={g.label} tone="neutral" size="sm" title={`${g.label} fit`}>
          {g.label}: {g.value}
        </Badge>
      ))}
    </span>
  );
}

function PickRow({ pick, projectId }: { pick: AdminSearchPick; projectId: string }) {
  return (
    <tr>
      <td className="rc-num">{pick.rank}</td>
      <td>
        <span className="rc-cell-strong">
          <TalentLink sellerId={pick.sellerId} projectId={projectId} />
        </span>
        {/* SCOUT ranks up to 12; the surface uses a subset. Saying which were
            approached is how the page shows a 12-pick shortlist without implying
            all 12 were contacted. Picked outranks both: the client's own press
            (a handoff row) is the strongest thing that can happen to a pick, so
            it is a SOLID BLUE pill — the accent tone is the success tone in this
            palette, and a soft "Picked" was the same tint as "Approached". Same
            pill as the Explorer project view, so the two pages read alike. */}
        {pick.picked ? (
          <Badge
            tone="info"
            variant="solid"
            size="sm"
            className="rc-count-badge"
            title="The client picked this talent: pressed their card or contacted them"
          >
            Picked
          </Badge>
        ) : pick.contacted ? (
          <Badge tone="success" size="sm" className="rc-count-badge">
            Approached
          </Badge>
        ) : (
          <Badge
            tone="neutral"
            size="sm"
            className="rc-count-badge"
            title="Ranked, held in reserve"
          >
            Reserve
          </Badge>
        )}
        {pick.coreProfession ? <div className="sr-sub">{pick.coreProfession}</div> : null}
      </td>
      <td>
        {/* The tier the shortlist was ACTUALLY ordered by: craft tier first, then
            shortfall severity, then (bucket, points) within it. */}
        {pick.fitCategory ? (
          <Badge tone="neutral" size="sm">
            {pick.fitCategory}
          </Badge>
        ) : (
          <span className="tm-unknown">Unknown</span>
        )}
        {pick.missesRank > 0 ? (
          <Badge
            tone="warning"
            size="sm"
            className="rc-count-badge"
            title="SCOUT's severity grade for how far this pick falls short of stated requirements"
          >
            misses {pick.missesRank}
          </Badge>
        ) : null}
        {pick.relaxed.length ? (
          <div className="sr-sub" title="Stated requirements this pick does not meet">
            relaxed: {pick.relaxed.join(", ")}
          </div>
        ) : null}
      </td>
      <td className="rc-num">
        {pick.qualitySortBucket != null ? (
          <span title="Quality bucket; lower is better. A bucket signal such as the recommended list.">
            {pick.qualitySortBucket.toFixed(0)}
          </span>
        ) : (
          <span className="tm-unknown">-</span>
        )}
      </td>
      <td className="rc-num">
        {pick.qualityPoints != null ? (
          <span title="Seller-quality points; orders talents within a craft tier">
            {pick.qualityPoints.toFixed(1)}
          </span>
        ) : (
          <span className="tm-unknown">-</span>
        )}
      </td>
      <td className="rc-num">
        {/* SCOUT's ordering key itself: fit-tier points + quality - shortfall penalty. Shown
            raw so it agrees with the rank AND means the same thing across searches; the older
            `pickScore` composes the same inputs differently (quality normalized against the
            pool, miss penalty capped) and disagreed with the order on 40% of searches, so it
            is only the fallback for rows written before the key was recorded. */}
        {pick.rankTotal != null ? (
          <span title="SCOUT's own ordering key: fit tier (2005 exact / 2000 strong / 1000 core / 500 adjacent) plus quality points, minus the shortfall penalty">
            {Number.isInteger(pick.rankTotal) ? pick.rankTotal : pick.rankTotal.toFixed(1)}
          </span>
        ) : pick.pickScore != null ? (
          <span title="This talent's combined fit and quality, 0-100 (legacy score: may not match the rank)">
            {(pick.pickScore * 100).toFixed(0)}
          </span>
        ) : (
          <span className="tm-unknown">-</span>
        )}
      </td>
      <td className="sr-reasoning">
        {/* SCOUT's verbatim output. An operator asking "why this talent" needs
            what the model actually said, so nothing here is rephrased. */}
        {pick.rationale ? <p>{pick.rationale}</p> : null}
        {pick.domainRelevance ? <p className="sr-reasoning__alt">{pick.domainRelevance}</p> : null}
        {pick.fitRationale && pick.fitRationale !== pick.rationale ? (
          <p className="sr-reasoning__alt">{pick.fitRationale}</p>
        ) : null}
        {!pick.rationale && !pick.domainRelevance && !pick.fitRationale ? (
          <span className="tm-unknown">No reasoning recorded</span>
        ) : null}
        <Scorecard pick={pick} />
      </td>
      <td>
        {pick.signals.length ? (
          <span className="sr-chips">
            {pick.signals.map((s) => (
              <Badge key={s} tone="neutral" size="sm">
                {s}
              </Badge>
            ))}
          </span>
        ) : (
          <span className="tm-unknown">-</span>
        )}
      </td>
      <td className="sr-gig">
        {pick.gigTitle ? (
          <>
            <div>{pick.gigTitle}</div>
            <div className="sr-sub">
              {pick.gigPrice ? `$${pick.gigPrice.toLocaleString()}` : "no price"}
              {pick.gigDeliveryDays ? ` · ${pick.gigDeliveryDays}d` : ""}
              {pick.chosenPackage ? ` · ${pick.chosenPackage}` : ""}
            </div>
          </>
        ) : (
          <span className="tm-unknown">-</span>
        )}
      </td>
      {/* The client's own Fiverr conversation with this talent, in Fiverr OS
          Chats Research — the same link (same backend, same two NUMERIC ids) the
          Explorer project view carries on its outreach threads and its picks, so
          a talent links identically wherever an operator meets them. A plain
          hyphen when either id is unknown: nothing to open, and a dead button
          invites the click anyway. */}
      <td>
        {pick.fiverrOsChatUrl ? (
          <a
            className="ex-os-chat-link"
            href={pick.fiverrOsChatUrl}
            target="_blank"
            rel="noreferrer noopener"
            title={`Open the client's Fiverr conversation with ${pick.sellerId} in Fiverr OS`}
          >
            Fiverr OS
          </a>
        ) : (
          <span className="tm-unknown">-</span>
        )}
      </td>
    </tr>
  );
}

export function SearchDetailPage({ searchId }: { searchId: string }) {
  const detail = useQuery(talentSearchQuery(searchId));

  if (detail.isPending) {
    return (
      <div className="ad-page">
        <LoadingState label="Loading search…" />
      </div>
    );
  }
  if (detail.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary
          area="talent_management"
          error={detail.error}
          onRetry={() => void detail.refetch()}
        />
      </div>
    );
  }

  const d = detail.data;
  return (
    <div className="ad-page">
      <header className="sr-head">
        <BackLink to="/talent-management/searches" label="Back to Searches" />
        <div>
          <h1 className="ad-page__title">
            {/* The title IS the project, so it opens the project. Falls back to
                plain text for a viewer the Explorer does not open for. */}
            <ExplorerProjectLink projectId={d.projectId} className="cs-primary-link">
              {d.projectName || "Untitled project"}
            </ExplorerProjectLink>
          </h1>
          <p className="ad-page__sub">
            Searched {formatDateTime(d.createdAt ?? null)} · <SurfaceBadge surface={d.surface} /> ·{" "}
            {d.poolCount.toLocaleString()} talents considered · {d.rankedCount.toLocaleString()}{" "}
            shortlisted
            {d.elapsedMs != null ? ` · took ${Math.round(d.elapsedMs / 1000)}s` : ""}
          </p>
        </div>
      </header>

      <section className="ad-section">
        <h2 className="ad-section__title">What Mira was looking for</h2>
        {d.querySummary ? (
          <p className="sr-prose">{d.querySummary}</p>
        ) : (
          <p className="sr-empty">No search brief was recorded.</p>
        )}
        {/* The queries actually sent to the marketplace, not a re-derivation: this
            is how an operator sees that a thin result came from a bad query. */}
        {d.searchTerms.length ? (
          <span className="sr-chips">
            {d.searchTerms.map((t) => (
              <Badge key={t} tone="neutral" size="sm" title="Query sent to the marketplace">
                {t}
              </Badge>
            ))}
          </span>
        ) : null}
      </section>

      <section className="ad-section">
        <h2 className="ad-section__title">Mira's reasoning</h2>
        {/* SCOUT's OWN rationale for the whole hunt. The page used to show a
            mechanical "Top match: X | n shortlisted" headcount here and call it the
            model's thinking; that line is gone rather than relabelled. */}
        {d.scoutRationale ? (
          <p className="sr-prose sr-prose--quote">{d.scoutRationale}</p>
        ) : (
          <p className="sr-empty">
            Mira did not record its reasoning for this search. Searches run before this was captured
            will not have it.
          </p>
        )}
      </section>

      <section className="ad-section">
        <h2 className="ad-section__title">Requirements</h2>
        <Requirements detail={d} />
      </section>

      <section className="ad-section">
        <div className="cs-section-head">
          <h2 className="ad-section__title">Talents Mira chose</h2>
          <span className="cs-section-head__note">
            {d.picks.length.toLocaleString()} ranked, in Mira's order
            {d.picks.some((p) => p.contacted)
              ? ` · ${d.picks.filter((p) => p.contacted).length} approached`
              : ""}
          </span>
        </div>
        {d.picks.length === 0 ? (
          <EmptyState
            title="This search produced no shortlist"
            message={
              d.degraded
                ? "The talent marketplace was unreachable, so this search never really ran. Worth retrying."
                : "Nobody cleared the requirements. Either the brief is too narrow or the marketplace has no match."
            }
          />
        ) : (
          <div className="rc-table-wrap">
            <table className="rc-table sr-picks">
              <TableExport name="search-picks" label="the picks" />
              <thead>
                <tr>
                  <th className="rc-num">#</th>
                  <th>Talent</th>
                  <th>Tier</th>
                  <th className="rc-num">Bucket</th>
                  <th className="rc-num">Points</th>
                  <th className="rc-num">Total</th>
                  <th>Why Mira picked them</th>
                  <th>Strengths</th>
                  <th>Best-fitting gig</th>
                  <th>Fiverr OS</th>
                </tr>
              </thead>
              <tbody>
                {d.picks.map((p) => (
                  <PickRow key={`${p.rank}-${p.sellerId}`} pick={p} projectId={d.projectId} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
